import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaSearch, FaSlidersH, FaChevronDown, FaChevronUp, FaPlus, FaTimes, FaSyncAlt, FaCog, FaSortAmountDown } from 'react-icons/fa';
import { FieldSelectSearch, FieldDate, FieldDateRange, FieldInput } from '../Form/_index';

const BLOCK = 'LStudio__app_table';

/*
    🔥 CABECERA DE SECCIÓN CON FILTROS AVANZADOS

    Pill, título con destacado, botón de alta, engranaje con acciones
    secundarias, búsqueda, orden y panel de filtros plegable.

    ⚠️ EL BOTÓN DE ALTA ES UN ENLACE

    Un alta es una PANTALLA, no un diálogo: tiene su propia URL, se
    comparte, se abre en pestaña nueva y se vuelve con el botón atrás.
    Un formulario largo dentro de un modal obliga a scrollear dos veces
    y tapa la tabla que uno estaba mirando.

    Se pasa `newTo`. `onNew` queda por si de verdad no navega.

    ⚠️ EL CONTENEDOR LOCAL DE PORTALES

    Los menús de react-select y del datepicker, si van al <body>,
    quedan en un contexto de apilado HERMANO del layout: ahí gana el
    último del DOM sin importar el z-index, y el menú se dibuja SOBRE
    el header de la app.

    Montándolos en un div propio DENTRO del layout, quedan en el flujo
    y el header los tapa como corresponde.

    ⚠️ LOS FILTROS SON DATOS, NO JSX

        { key, label, type, value, onChange, options, placeholder, col }

    type: text | select | multi | date | dateRange | number

    Así la vista declara QUÉ filtros hay, y este componente decide
    cómo se dibujan.
*/

const PORTAL_ID = 'LStudio__portal_local';

const FilterField = ({ filter }) => {

    const COMMON = {
        id: `filter_${filter.key}`,
        label: null,
        hint: null,
        span: 1,
        isDisabled: filter.isDisabled,
    };

    if (filter.type === 'date') {
        return (
            <FieldDate {...COMMON}
                value={filter.value} onChange={filter.onChange}
                placeholder={filter.placeholder ?? 'Elegí una fecha'} />
        );
    }

    if (filter.type === 'dateRange') {
        return (
            <FieldDateRange {...COMMON}
                value={filter.value ?? [null, null]} onChange={filter.onChange}
                placeholder={filter.placeholder ?? 'Elegí un rango'} />
        );
    }

    if (filter.type === 'multi' || filter.type === 'select') {
        return (
            <FieldSelectSearch {...COMMON}
                isMulti={filter.type === 'multi'}
                options={filter.options ?? []}
                value={filter.value} onChange={filter.onChange}
                placeholder={filter.placeholder ?? 'Seleccionar…'}
                noOptionsText="Sin opciones" />
        );
    }

    if (filter.type === 'number') {
        return (
            <FieldInput {...COMMON} type="number"
                value={filter.value ?? ''} onChange={filter.onChange}
                placeholder={filter.placeholder} />
        );
    }

    return (
        <FieldInput {...COMMON}
            value={filter.value ?? ''} onChange={filter.onChange}
            placeholder={filter.placeholder} />
    );
};

/*
    🔥 Menú del engranaje

    Sus items también aceptan `to`: "Permisos" o "Configuración" son
    pantallas, no acciones.
*/
const GearMenu = ({ items = [], isDisabled = false }) => {

    const REF__useRefBox = useRef(null);

    const [useIsOpen, setIsOpen] = useState(false);

    useEffect(() => {

        if (!useIsOpen) return;

        const onClickOutside = (event) => {
            if (REF__useRefBox.current && !REF__useRefBox.current.contains(event.target)) setIsOpen(false);
        };

        const onEscape = (event) => { if (event.key === 'Escape') setIsOpen(false); };

        document.addEventListener('mousedown', onClickOutside);
        document.addEventListener('keydown', onEscape);

        return () => {
            document.removeEventListener('mousedown', onClickOutside);
            document.removeEventListener('keydown', onEscape);
        };

    }, [useIsOpen]);

    if (!items.length) return null;

    const onRenderItem = (item, index) => {

        if (item.isDivider) return <span key={`div_${index}`} className={`${BLOCK}__float_divider`} />;

        const CONTENT = (
            <>
                {item.icon && <span className={`${BLOCK}__float_icon`}>{item.icon}</span>}
                <span className={`${BLOCK}__float_text`}>{item.label}</span>
                {item.hint && <small>{item.hint}</small>}
            </>
        );

        if (item.to && !item.isDisabled) {
            return (
                <Link key={item.label} to={item.to} role="menuitem"
                    className={`${BLOCK}__float_item`} data-tone={item.tone}
                    onClick={() => setIsOpen(false)} >
                    {CONTENT}
                </Link>
            );
        }

        return (
            <button key={item.label} type="button" role="menuitem"
                className={`${BLOCK}__float_item`} data-tone={item.tone}
                disabled={item.isDisabled}
                onClick={() => { setIsOpen(false); item.onClick?.(); }} >
                {CONTENT}
            </button>
        );
    };

    return (
        <div className={`${BLOCK}__gear_wrap`} ref={REF__useRefBox}>

            <button type="button" className={`${BLOCK}__gear`} data-open={useIsOpen}
                aria-label="Más acciones" aria-haspopup="menu" aria-expanded={useIsOpen}
                disabled={isDisabled}
                onClick={() => { if (!isDisabled) setIsOpen(!useIsOpen); }} >
                <FaCog />
            </button>

            {
                useIsOpen && (
                    <div className={`${BLOCK}__gear_menu`} role="menu">
                        {items.map(onRenderItem)}
                    </div>
                )
            }

        </div>
    );
};

export const TableHeader = ({
    // identidad de la sección
    pill = 'SECCIÓN',
    title = 'Gestión de',
    titleAccent = 'registros',
    subtitle = '',

    // acción principal
    newLabel = 'Nuevo',
    newTo = null,                 // ⚠️ preferí esto: navega, no abre modal
    onNew = null,                 // solo si de verdad no navega
    hasNew = true,

    // acciones secundarias
    gearItems = [],

    // búsqueda
    search = '',
    onSearch = null,
    searchPlaceholder = '¿Qué buscás hoy?',

    // orden
    orderOptions = [],
    order = null,
    onOrder = null,

    // filtros avanzados
    filters = [],
    onClearFilters = null,

    // extras
    toolbar = null,
    stats = null,
    isDisabled = false,
}) => {

    const [useIsFiltersOpen, setIsFiltersOpen] = useState(false);

    const REF__useRefOrderSelect = useRef(null);

    /*
        Toda la capsula de Ordenar es interactiva. El select conserva su
        implementacion del template; solo exponemos su ref para abrir el menu
        cuando el usuario pulsa sobre el icono, la etiqueta o cualquier hueco
        libre de la capsula.
    */
    const onOrderMouseDown = (event) => {

        if (isDisabled || event.button !== 0) return;

        // Si el click ya ocurrio sobre React Select, dejamos que lo resuelva.
        if (event.target.closest?.(`.${BLOCK}__order_select`)) return;

        event.preventDefault();

        const SELECT = REF__useRefOrderSelect.current;

        SELECT?.focus?.();
        SELECT?.openMenu?.('first');
    };

    /*
        ⚠️ Un filtro "activo" no es lo mismo según el tipo: un multi
           está activo con al menos un elegido, un texto con contenido,
           y un rango con al menos un extremo.
    */
    const onIsActive = (filter) => {

        const V = filter.value;

        if (V === null || V === undefined || V === '') return false;

        if (Array.isArray(V)) return V.some((item) => item !== null && item !== undefined);

        return true;
    };

    const ACTIVE_COUNT = filters.filter(onIsActive).length + (search ? 1 : 0);

    const HAS_FILTERS = ACTIVE_COUNT > 0;

    const SELECTED_ORDER = orderOptions.find((option) => option.value === order) ?? orderOptions[0] ?? null;

    return (
        <div className={`${BLOCK}__header`} data-disabled={isDisabled ? "true" : "false"}>

            {/* ==================== 🔥 IDENTIDAD ==================== */}
            <div className={`${BLOCK}__header_top`}>

                <div className={`${BLOCK}__header_text`}>

                    <span className={`${BLOCK}__pill`}>
                        <span className={`${BLOCK}__pill_dot`} aria-hidden="true" />
                        {pill}
                    </span>

                    <h3 className={`${BLOCK}__title`}>
                        {title} <span className={`${BLOCK}__title_accent`}>{titleAccent}</span>
                    </h3>

                    {subtitle && <p className={`${BLOCK}__subtitle`}>{subtitle}</p>}

                </div>

                <div className={`${BLOCK}__header_actions`}>

                    {toolbar}

                    {
                        hasNew && (newTo || onNew) && (
                            newTo
                                ? (
                                    <Link to={newTo} className={`${BLOCK}__new`}
                                        aria-disabled={isDisabled ? 'true' : undefined}
                                        tabIndex={isDisabled ? -1 : undefined}
                                        onClick={(event) => { if (isDisabled) event.preventDefault(); }}>
                                        <FaPlus /> {newLabel}
                                    </Link>
                                )
                                : (
                                    <button type="button" className={`${BLOCK}__new`} onClick={onNew} disabled={isDisabled}>
                                        <FaPlus /> {newLabel}
                                    </button>
                                )
                        )
                    }

                    <GearMenu items={gearItems} isDisabled={isDisabled} />

                </div>

            </div>

            {stats && <div className={`${BLOCK}__header_stats`}>{stats}</div>}

            {/* ==================== 🔥 BARRA DE FILTROS ==================== */}
            <div className={`${BLOCK}__filters_bar`}>

                {
                    onSearch && (
                        <div className={`${BLOCK}__search`}>
                            <FaSearch aria-hidden="true" />
                            <input type="search" value={search}
                                disabled={isDisabled}
                                onChange={(event) => onSearch(event.target.value)}
                                placeholder={searchPlaceholder}
                                aria-label="Buscar" />
                            {
                                search && (
                                    <button type="button" aria-label="Limpiar búsqueda"
                                        disabled={isDisabled}
                                        onClick={() => onSearch('')} >
                                        <FaTimes />
                                    </button>
                                )
                            }
                        </div>
                    )
                }

                {
                    orderOptions.length > 0 && (
                        <div className={`${BLOCK}__order`} onMouseDown={onOrderMouseDown}>
                            <FaSortAmountDown aria-hidden="true" />
                            <span>Ordenar:</span>
                            <div className={`${BLOCK}__order_select`}>
                                <FieldSelectSearch
                                    id="table_order"
                                    selectRef={REF__useRefOrderSelect}
                                    label={null}
                                    options={orderOptions}
                                    value={SELECTED_ORDER}
                                    onChange={(option) => onOrder?.(option?.value ?? '')}
                                    isClearable={false}
                                    isSearchable={false}
                                    isDisabled={isDisabled}
                                    openMenuOnFocus
                                    placeholder="Ordenar…"
                                    noOptionsText="Sin opciones"
                                />
                            </div>
                        </div>
                    )
                }

                {
                    filters.length > 0 && (
                        <button type="button" className={`${BLOCK}__filters_toggle`}
                            disabled={isDisabled}
                            data-on={useIsFiltersOpen}
                            aria-expanded={useIsFiltersOpen}
                            onClick={() => setIsFiltersOpen(!useIsFiltersOpen)} >
                            <FaSlidersH /> Filtros
                            {ACTIVE_COUNT > 0 && <span className={`${BLOCK}__filters_count`}>{ACTIVE_COUNT}</span>}
                            {useIsFiltersOpen ? <FaChevronUp /> : <FaChevronDown />}
                        </button>
                    )
                }

                {
                    HAS_FILTERS && onClearFilters && (
                        <button type="button" className={`${BLOCK}__clear`} disabled={isDisabled} onClick={onClearFilters}>
                            <FaSyncAlt /> Limpiar
                        </button>
                    )
                }

            </div>

            {/* ==================== 🔥 PANEL AVANZADO ==================== */}
            {
                filters.length > 0 && (
                    <div className={`${BLOCK}__filters_panel`} data-open={useIsFiltersOpen}>

                        <div className={`${BLOCK}__filters_grid`}>
                            {
                                filters.map((filter) => (
                                    <div key={filter.key} className={`${BLOCK}__filter`}
                                        style={{ '--LS__filter_span': filter.col ?? 1 }} >

                                        <label className={`${BLOCK}__filter_label`}
                                            htmlFor={`filter_${filter.key}`} >
                                            {filter.label}
                                            {onIsActive(filter) && <span className={`${BLOCK}__filter_dot`} />}
                                        </label>

                                        <FilterField filter={{ ...filter, isDisabled: isDisabled || filter.isDisabled }} />

                                    </div>
                                ))
                            }
                        </div>

                    </div>
                )
            }

            {/* 🔥 Contenedor local para los menús flotantes */}
            <div id={PORTAL_ID} />

        </div>
    );
};
